import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Shield,
  FileText,
  Users,
  AlertTriangle,
  Clock,
  Target,
  Swords,
  CheckCircle,
  XCircle,
  ArrowRight,
  Brain,
  X,
  Paperclip,
  Upload,
  Download,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { EvidenceUploadModal, EvidenceList, EvidenceBadge } from './EvidencePanel';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ─── Custom Node Components ─── */

const CaseHubNode = ({ data }) => (
  <div className="relative px-5 py-4 rounded-2xl border-2 border-blue-600 bg-gradient-to-br from-blue-50 to-indigo-100 shadow-lg min-w-[180px] max-w-[240px] text-center cursor-pointer">
    <Handle type="source" position={Position.Right} className="!bg-blue-600 !w-2.5 !h-2.5" />
    <Handle type="source" position={Position.Bottom} className="!bg-blue-600 !w-2.5 !h-2.5" id="bottom" />
    <Handle type="source" position={Position.Left} className="!bg-blue-600 !w-2.5 !h-2.5" id="left" />
    <Handle type="source" position={Position.Top} className="!bg-blue-600 !w-2.5 !h-2.5" id="top" />
    <Brain className="w-8 h-8 mx-auto text-blue-700 mb-1" />
    <p className="text-sm font-bold text-blue-900 leading-tight">{data.label}</p>
    <Badge className={`mt-1 text-xs ${data.riskColor}`}>{data.risk}</Badge>
  </div>
);

const ClaimNode = ({ data }) => (
  <div className="relative px-4 py-3 rounded-xl border-2 border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50 shadow-md min-w-[160px] max-w-[220px] cursor-pointer">
    <Handle type="target" position={Position.Left} className="!bg-orange-500 !w-2 !h-2" />
    <Handle type="source" position={Position.Right} className="!bg-orange-500 !w-2 !h-2" />
    <Handle type="source" position={Position.Bottom} className="!bg-orange-500 !w-2 !h-2" id="bottom" />
    <EvidenceBadge count={data.evidenceCount || 0} hasAffidavit={data.hasAffidavit} isMissing={data.evidenceMissing} />
    <div className="flex items-center gap-1.5 mb-1">
      <Target className="w-4 h-4 text-orange-600 flex-shrink-0" />
      <span className="text-xs font-bold text-orange-900 leading-tight">{data.label}</span>
    </div>
    <Badge variant="outline" className="text-[10px] capitalize">{(data.type || '').replace(/_/g, ' ')}</Badge>
    {data.rebuttalStatus && (
      <Badge variant="outline" className={`ml-1 text-[10px] ${data.rebuttalStatus === 'fully_rebutted' ? 'border-green-400 text-green-700' : data.rebuttalStatus === 'not_rebutted' ? 'border-red-400 text-red-700' : 'border-yellow-400 text-yellow-700'}`}>
        {(data.rebuttalStatus || '').replace(/_/g, ' ')}
      </Badge>
    )}
  </div>
);

const PartyNode = ({ data }) => (
  <div className="relative px-4 py-3 rounded-xl border-2 border-indigo-400 bg-gradient-to-br from-indigo-50 to-violet-50 shadow-md min-w-[150px] max-w-[210px] cursor-pointer">
    <Handle type="target" position={Position.Left} className="!bg-indigo-500 !w-2 !h-2" />
    <Handle type="source" position={Position.Right} className="!bg-indigo-500 !w-2 !h-2" />
    <Handle type="source" position={Position.Bottom} className="!bg-indigo-500 !w-2 !h-2" id="bottom" />
    <EvidenceBadge count={data.evidenceCount || 0} isMissing={data.evidenceMissing} />
    <div className="flex items-center gap-1.5 mb-1">
      <Users className="w-4 h-4 text-indigo-600 flex-shrink-0" />
      <span className="text-xs font-bold text-indigo-900 leading-tight">{data.label}</span>
    </div>
    <Badge variant="outline" className="text-[10px] capitalize">{(data.role || '').replace(/_/g, ' ')}</Badge>
    {data.hasResponded ? (
      <Badge className="ml-1 text-[10px] bg-green-100 text-green-700">Responded</Badge>
    ) : (
      <Badge className="ml-1 text-[10px] bg-red-100 text-red-700">No Response</Badge>
    )}
  </div>
);

const DocumentNode = ({ data }) => (
  <div className="relative px-3 py-2 rounded-lg border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm min-w-[130px] max-w-[190px] cursor-pointer">
    <Handle type="target" position={Position.Left} className="!bg-emerald-500 !w-2 !h-2" />
    <Handle type="target" position={Position.Top} className="!bg-emerald-500 !w-2 !h-2" id="top" />
    <Handle type="source" position={Position.Right} className="!bg-emerald-500 !w-2 !h-2" />
    <EvidenceBadge count={data.evidenceCount || 0} hasDeliveryProof={data.hasDeliveryProof} isMissing={data.evidenceMissing} />
    <div className="flex items-center gap-1.5">
      <FileText className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
      <span className="text-[11px] font-medium text-emerald-900 leading-tight truncate">{data.label}</span>
    </div>
    {data.tag && <Badge variant="outline" className="text-[9px] mt-1 capitalize">{data.tag}</Badge>}
  </div>
);

const WeakLinkNode = ({ data }) => (
  <div className="relative px-4 py-3 rounded-xl border-2 border-red-400 bg-gradient-to-br from-red-50 to-rose-50 shadow-md min-w-[150px] max-w-[210px] cursor-pointer animate-pulse-subtle">
    <Handle type="target" position={Position.Top} className="!bg-red-500 !w-2 !h-2" />
    <Handle type="target" position={Position.Left} className="!bg-red-500 !w-2 !h-2" id="left" />
    <div className="flex items-center gap-1.5 mb-1">
      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
      <span className="text-xs font-bold text-red-900 leading-tight">{data.label}</span>
    </div>
    <Badge variant="outline" className="text-[10px] capitalize border-red-300 text-red-700">{(data.weakType || '').replace(/_/g, ' ')}</Badge>
  </div>
);

const TimelineNode = ({ data }) => (
  <div className="relative px-3 py-2 rounded-lg border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-fuchsia-50 shadow-sm min-w-[140px] max-w-[200px] cursor-pointer">
    <Handle type="target" position={Position.Left} className="!bg-purple-500 !w-2 !h-2" />
    <Handle type="source" position={Position.Right} className="!bg-purple-500 !w-2 !h-2" />
    <EvidenceBadge count={data.evidenceCount || 0} isMissing={data.evidenceMissing} />
    <div className="flex items-center gap-1.5 mb-0.5">
      <Clock className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
      <span className="text-[10px] font-mono text-purple-700">{data.date}</span>
    </div>
    <p className="text-[11px] font-medium text-purple-900 leading-tight">{data.label}</p>
    <Badge variant="outline" className="text-[9px] mt-0.5 capitalize">{(data.eventType || '').replace(/_/g, ' ')}</Badge>
  </div>
);

const PendingNode = ({ data }) => (
  <div className="relative px-4 py-3 rounded-xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-md min-w-[150px] max-w-[210px] cursor-pointer">
    <Handle type="target" position={Position.Top} className="!bg-amber-500 !w-2 !h-2" />
    <EvidenceBadge count={data.evidenceCount || 0} isMissing={data.evidenceMissing} />
    <div className="flex items-center gap-1.5 mb-1">
      <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <span className="text-xs font-bold text-amber-900 leading-tight">{data.label}</span>
    </div>
    <Badge className={`text-[10px] ${data.priority === 'high' ? 'bg-red-600' : data.priority === 'medium' ? 'bg-yellow-600' : 'bg-gray-500'} text-white`}>
      {data.priority}
    </Badge>
  </div>
);

const OpposingNode = ({ data }) => (
  <div className="relative px-4 py-3 rounded-xl border-2 border-slate-400 bg-gradient-to-br from-slate-50 to-gray-100 shadow-md min-w-[150px] max-w-[210px] cursor-pointer">
    <Handle type="target" position={Position.Left} className="!bg-slate-500 !w-2 !h-2" />
    <div className="flex items-center gap-1.5 mb-1">
      <Swords className="w-4 h-4 text-slate-600 flex-shrink-0" />
      <span className="text-xs font-bold text-slate-900 leading-tight">{data.label}</span>
    </div>
    <Badge variant="outline" className="text-[10px] capitalize">{data.likelihood} likelihood</Badge>
  </div>
);

const NODE_TYPES = {
  caseHub: CaseHubNode,
  claim: ClaimNode,
  party: PartyNode,
  document: DocumentNode,
  weakLink: WeakLinkNode,
  timeline: TimelineNode,
  pending: PendingNode,
  opposing: OpposingNode,
};

const RISK_BADGE_COLORS = {
  low: 'bg-green-100 text-green-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

/* ─── Detail Panel (appears on node click) ─── */
const DetailPanel = ({ node, onClose, nodeEvidence, onUploadClick, onDeleteEvidence, onDownloadEvidence }) => {
  if (!node) return null;
  const d = node.data;
  const evidence = nodeEvidence || [];
  return (
    <Card className="absolute top-4 right-4 z-50 w-80 shadow-xl border-2 border-gray-200 max-h-[90%] overflow-y-auto" data-testid="node-detail-panel">
      <CardHeader className="pb-2 flex flex-row items-start justify-between">
        <CardTitle className="text-sm">{d.label}</CardTitle>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="text-xs space-y-2">
        {d.description && <p className="text-gray-700">{d.description}</p>}
        {d.evidence && <p className="text-gray-600"><span className="font-semibold">Evidence:</span> {d.evidence}</p>}
        {d.recommendation && <p className="text-blue-700"><span className="font-semibold">Recommendation:</span> {d.recommendation}</p>}
        {d.significance && <p className="text-purple-700"><span className="font-semibold">Significance:</span> {d.significance}</p>}
        {d.preparation && <p className="text-slate-700"><span className="font-semibold">Prepare:</span> {d.preparation}</p>}
        {d.reason && <p className="text-gray-600"><span className="font-semibold">Reason:</span> {d.reason}</p>}
        {d.sourceDocument && <p className="text-emerald-700"><span className="font-semibold">Source:</span> {d.sourceDocument}</p>}
        {d.linkedDocument && <p className="text-emerald-700"><span className="font-semibold">Document:</span> {d.linkedDocument}</p>}
        {d.deadline && <p className="text-orange-700"><span className="font-semibold">Deadline:</span> {d.deadline}</p>}
        {d.docsConnected?.length > 0 && (
          <div>
            <span className="font-semibold">Connected Docs:</span>
            <ul className="list-disc ml-4 mt-1 text-gray-600">
              {d.docsConnected.map((doc, i) => <li key={i}>{doc}</li>)}
            </ul>
          </div>
        )}

        {/* Evidence Section */}
        <div className="border-t pt-2 mt-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-bold text-gray-700 flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              Evidence ({evidence.length})
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => onUploadClick(node)}
              data-testid="attach-evidence-btn"
            >
              <Upload className="w-3 h-3 mr-1" />
              Attach
            </Button>
          </div>
          <EvidenceList
            evidence={evidence}
            onDelete={onDeleteEvidence}
            onDownload={onDownloadEvidence}
          />
          {evidence.length === 0 && (
            <div className="flex items-center gap-1 mt-1 p-1.5 bg-amber-50 rounded text-[10px] text-amber-700">
              <AlertTriangle className="w-3 h-3" />
              No evidence attached — click Attach to add
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── Layout Engine ─── */
function buildGraph(analysis) {
  const nodes = [];
  const edges = [];
  let nodeId = 0;
  const nid = () => `n-${nodeId++}`;

  if (!analysis) return { nodes: [], edges: [] };

  const riskLevel = analysis.overall_risk_level || 'moderate';

  // Central hub
  const hubId = nid();
  nodes.push({
    id: hubId,
    type: 'caseHub',
    position: { x: 500, y: 350 },
    data: {
      label: 'Case Overview',
      risk: riskLevel.toUpperCase(),
      riskColor: RISK_BADGE_COLORS[riskLevel] || RISK_BADGE_COLORS.moderate,
      description: analysis.case_summary,
    },
  });

  // ─── Claims (right of hub) ───
  const claims = analysis.claims || [];
  const claimIds = [];
  claims.forEach((c, i) => {
    const id = nid();
    claimIds.push(id);
    nodes.push({
      id,
      type: 'claim',
      position: { x: 820, y: 150 + i * 130 },
      data: {
        label: c.title,
        type: c.type,
        rebuttalStatus: c.rebuttal_status,
        description: c.description,
        evidence: c.supporting_evidence,
        sourceDocument: c.source_document,
      },
    });
    edges.push({
      id: `e-hub-claim-${i}`,
      source: hubId,
      target: id,
      animated: true,
      style: { stroke: '#f97316', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#f97316' },
    });
  });

  // ─── Parties (left of hub) ───
  const parties = analysis.parties || [];
  parties.forEach((p, i) => {
    const id = nid();
    nodes.push({
      id,
      type: 'party',
      position: { x: 100, y: 150 + i * 130 },
      data: {
        label: p.name,
        role: p.role,
        hasResponded: p.has_responded,
        description: p.description,
        docsConnected: p.documents_connected,
      },
    });
    edges.push({
      id: `e-hub-party-${i}`,
      source: hubId,
      sourceHandle: 'left',
      target: id,
      style: { stroke: '#6366f1', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
    });
  });

  // ─── Documents (below claims, connected to claims/parties) ───
  const docNames = new Set();
  claims.forEach(c => { if (c.source_document) docNames.add(c.source_document); });
  parties.forEach(p => (p.documents_connected || []).forEach(d => docNames.add(d)));
  (analysis.timeline || []).forEach(t => { if (t.linked_document) docNames.add(t.linked_document); });

  const docIdMap = {};
  let docIdx = 0;
  Array.from(docNames).forEach((name) => {
    const id = nid();
    docIdMap[name] = id;
    nodes.push({
      id,
      type: 'document',
      position: { x: 850 + (docIdx % 2) * 200, y: 150 + claims.length * 130 + 40 + Math.floor(docIdx / 2) * 80 },
      data: { label: name.length > 30 ? name.substring(0, 28) + '...' : name, tag: 'evidence', fullName: name },
    });
    docIdx++;
  });

  // Connect documents to claims
  claims.forEach((c, i) => {
    if (c.source_document && docIdMap[c.source_document]) {
      edges.push({
        id: `e-claim-doc-${i}`,
        source: claimIds[i],
        target: docIdMap[c.source_document],
        style: { stroke: '#10b981', strokeWidth: 1.5, strokeDasharray: '5 3' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
      });
    }
  });

  // ─── Weak Links (below hub) ───
  const weakLinks = analysis.weakest_links || [];
  weakLinks.forEach((wl, i) => {
    const id = nid();
    nodes.push({
      id,
      type: 'weakLink',
      position: { x: 350 + i * 240, y: 600 },
      data: {
        label: wl.title,
        weakType: wl.type,
        description: wl.description,
        recommendation: wl.recommendation,
      },
    });
    edges.push({
      id: `e-hub-weak-${i}`,
      source: hubId,
      sourceHandle: 'bottom',
      target: id,
      style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '6 4' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' },
      animated: true,
    });
  });

  // ─── Timeline (top of hub) ───
  const timeline = analysis.timeline || [];
  timeline.forEach((t, i) => {
    const id = nid();
    nodes.push({
      id,
      type: 'timeline',
      position: { x: 150 + i * 230, y: 20 },
      data: {
        label: t.event.length > 40 ? t.event.substring(0, 38) + '...' : t.event,
        date: t.date,
        eventType: t.type,
        significance: t.significance,
        linkedDocument: t.linked_document,
      },
    });
    // Chain timeline events horizontally
    if (i > 0) {
      edges.push({
        id: `e-timeline-${i}`,
        source: `n-${nodeId - 2}`,
        target: id,
        style: { stroke: '#a855f7', strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#a855f7' },
      });
    }
  });
  // Connect first timeline to hub
  if (timeline.length > 0) {
    edges.push({
      id: 'e-hub-timeline',
      source: hubId,
      sourceHandle: 'top',
      target: nodes.find(n => n.type === 'timeline')?.id,
      style: { stroke: '#a855f7', strokeWidth: 1.5, strokeDasharray: '4 3' },
    });
  }

  // ─── Pending Steps (far right bottom) ───
  const pending = analysis.pending_steps || [];
  pending.forEach((s, i) => {
    const id = nid();
    nodes.push({
      id,
      type: 'pending',
      position: { x: 900 + (i % 2) * 220, y: 550 + Math.floor(i / 2) * 100 },
      data: {
        label: s.step.length > 40 ? s.step.substring(0, 38) + '...' : s.step,
        priority: s.priority,
        description: s.reason,
        deadline: s.deadline,
      },
    });
    // Connect to a claim or hub
    const targetSource = claimIds.length > 0 ? claimIds[i % claimIds.length] : hubId;
    edges.push({
      id: `e-pending-${i}`,
      source: targetSource,
      sourceHandle: 'bottom',
      target: id,
      style: { stroke: '#f59e0b', strokeWidth: 1.5, strokeDasharray: '4 3' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#f59e0b' },
    });
  });

  // ─── Predicted Opposing Moves (far left bottom) ───
  const opposing = analysis.predicted_opposing_moves || [];
  opposing.forEach((pm, i) => {
    const id = nid();
    nodes.push({
      id,
      type: 'opposing',
      position: { x: 20, y: 550 + i * 110 },
      data: {
        label: pm.move.length > 40 ? pm.move.substring(0, 38) + '...' : pm.move,
        likelihood: pm.likelihood,
        reason: pm.reason,
        preparation: pm.preparation,
      },
    });
    edges.push({
      id: `e-hub-opposing-${i}`,
      source: hubId,
      sourceHandle: 'left',
      target: id,
      style: { stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '8 4' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
    });
  });

  return { nodes, edges };
}

/* ─── Main Component ─── */
const BattlefieldMap = ({ analysis, conversationId }) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => buildGraph(analysis), [analysis]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [evidenceMap, setEvidenceMap] = useState({});
  const [allEvidence, setAllEvidence] = useState([]);
  const [uploadTarget, setUploadTarget] = useState(null);

  // Fetch all evidence for this conversation
  const fetchEvidence = useCallback(async () => {
    if (!conversationId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/strategy-brain/${conversationId}/evidence`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data.success) {
        setAllEvidence(res.data.evidence || []);
        // Build map by node_id
        const map = {};
        for (const ev of (res.data.evidence || [])) {
          const nid = ev.node_id || 'unlinked';
          if (!map[nid]) map[nid] = [];
          map[nid].push(ev);
        }
        setEvidenceMap(map);

        // Update node data with evidence counts
        setNodes(prev => prev.map(n => {
          const nodeEvidence = map[n.id] || [];
          const count = nodeEvidence.length;
          const hasDeliveryProof = nodeEvidence.some(e => ['receipt', 'delivery_confirmation', 'tracking_confirmation'].includes(e.document_type));
          const hasAffidavit = nodeEvidence.some(e => ['affidavit', 'sworn_statement'].includes(e.document_type));
          // Claims and mailings without evidence are flagged
          const isCriticalType = ['claim', 'document', 'pending'].includes(n.type);
          const evidenceMissing = isCriticalType && count === 0;
          return {
            ...n,
            data: { ...n.data, evidenceCount: count, hasDeliveryProof, hasAffidavit, evidenceMissing }
          };
        }));
      }
    } catch (err) {
      console.error('Failed to fetch evidence:', err);
    }
  }, [conversationId, setNodes]);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleDeleteEvidence = useCallback(async (evidenceId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/strategy-brain/${conversationId}/evidence/${evidenceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchEvidence();
    } catch (err) {
      console.error('Failed to delete evidence:', err);
    }
  }, [conversationId, fetchEvidence]);

  const handleDownloadEvidence = useCallback(async (evidenceId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/strategy-brain/${conversationId}/evidence/${evidenceId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data.success && res.data.content) {
        const byteChars = atob(res.data.content);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: res.data.mime_type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || res.data.filename || 'evidence';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download evidence:', err);
    }
  }, [conversationId]);

  if (!analysis) return null;

  const selectedNodeEvidence = selectedNode ? (evidenceMap[selectedNode.id] || []) : [];

  return (
    <div className="relative w-full h-full" data-testid="battlefield-map">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Controls position="bottom-right" />
        <MiniMap
          nodeStrokeWidth={3}
          nodeColor={(node) => {
            switch (node.type) {
              case 'caseHub': return '#3b82f6';
              case 'claim': return '#f97316';
              case 'party': return '#6366f1';
              case 'document': return '#10b981';
              case 'weakLink': return '#ef4444';
              case 'timeline': return '#a855f7';
              case 'pending': return '#f59e0b';
              case 'opposing': return '#64748b';
              default: return '#999';
            }
          }}
          position="bottom-left"
          style={{ background: '#f8fafc' }}
        />
        <Background variant="dots" gap={20} size={1} color="#e2e8f0" />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm border rounded-lg p-2.5 shadow-sm z-10" data-testid="map-legend">
        <p className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Legend</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {[
            { color: 'bg-blue-500', label: 'Case Hub' },
            { color: 'bg-orange-500', label: 'Claims' },
            { color: 'bg-indigo-500', label: 'Parties' },
            { color: 'bg-emerald-500', label: 'Documents' },
            { color: 'bg-red-500', label: 'Weak Links' },
            { color: 'bg-purple-500', label: 'Timeline' },
            { color: 'bg-amber-500', label: 'Pending' },
            { color: 'bg-slate-500', label: 'Opposing' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
              <span className="text-[10px] text-gray-600">{label}</span>
            </div>
          ))}
        </div>
        {allEvidence.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t">
            <p className="text-[9px] text-gray-500">{allEvidence.length} evidence file(s) attached</p>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      <DetailPanel
        node={selectedNode}
        onClose={() => setSelectedNode(null)}
        nodeEvidence={selectedNodeEvidence}
        onUploadClick={(node) => setUploadTarget(node)}
        onDeleteEvidence={handleDeleteEvidence}
        onDownloadEvidence={handleDownloadEvidence}
      />

      {/* Upload Modal */}
      {uploadTarget && (
        <EvidenceUploadModal
          conversationId={conversationId}
          nodeId={uploadTarget.id}
          nodeType={uploadTarget.type}
          nodeLabel={uploadTarget.data?.label}
          onClose={() => setUploadTarget(null)}
          onUploaded={fetchEvidence}
        />
      )}
    </div>
  );
};

export default BattlefieldMap;
